import { matchesAnywhere } from './matching';
import { COLS, Cell, Grid, ROWS, Tile, TileKind } from './types';

let nextTileId = 1;

export function newTile(kind: TileKind): Tile {
  return { id: nextTileId++, kind };
}

export function randomKind(rng: () => number, kinds: readonly TileKind[]): TileKind {
  return kinds[Math.floor(rng() * kinds.length)];
}

export function createBoard(
  kinds: readonly TileKind[],
  rng: () => number = Math.random,
): Grid {
  const grid: Grid = Array.from({ length: ROWS }, () =>
    Array.from({ length: COLS }, () => null as Tile | null),
  );
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      grid[r][c] = newTile(pickNonMatchingKind(grid, r, c, kinds, rng));
    }
  }
  if (matchesAnywhere(grid)) {
    return createBoard(kinds, rng);
  }
  return grid;
}

function pickNonMatchingKind(
  grid: Grid,
  r: number,
  c: number,
  kinds: readonly TileKind[],
  rng: () => number,
): TileKind {
  const forbidden = new Set<TileKind>();
  if (c >= 2 && grid[r][c - 1]?.kind === grid[r][c - 2]?.kind) {
    const v = grid[r][c - 1]?.kind;
    if (v !== undefined && v !== null) forbidden.add(v);
  }
  if (r >= 2 && grid[r - 1][c]?.kind === grid[r - 2][c]?.kind) {
    const v = grid[r - 1][c]?.kind;
    if (v !== undefined && v !== null) forbidden.add(v);
  }
  let attempts = 0;
  while (attempts < 20) {
    const kind = randomKind(rng, kinds);
    if (!forbidden.has(kind)) return kind;
    attempts++;
  }
  return kinds[0];
}

export function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < ROWS && c >= 0 && c < COLS;
}

export function areAdjacent(a: Cell, b: Cell): boolean {
  const dr = Math.abs(a.row - b.row);
  const dc = Math.abs(a.col - b.col);
  return dr + dc === 1;
}

export function swapCells(grid: Grid, a: Cell, b: Cell): void {
  const tmp = grid[a.row][a.col];
  grid[a.row][a.col] = grid[b.row][b.col];
  grid[b.row][b.col] = tmp;
}

export type FallMove = { tile: Tile; fromRow: number; toRow: number; col: number };
export type Spawn = { tile: Tile; row: number; col: number };

export function applyGravity(grid: Grid): FallMove[] {
  const moves: FallMove[] = [];
  for (let c = 0; c < COLS; c++) {
    let writeRow = ROWS - 1;
    for (let r = ROWS - 1; r >= 0; r--) {
      const tile = grid[r][c];
      if (tile) {
        if (writeRow !== r) {
          grid[writeRow][c] = tile;
          grid[r][c] = null;
          moves.push({ tile, fromRow: r, toRow: writeRow, col: c });
        }
        writeRow--;
      }
    }
  }
  return moves;
}

export function refill(
  grid: Grid,
  kinds: readonly TileKind[],
  rng: () => number = Math.random,
): Spawn[] {
  const spawns: Spawn[] = [];
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      if (!grid[r][c]) {
        const tile = newTile(randomKind(rng, kinds));
        grid[r][c] = tile;
        spawns.push({ tile, row: r, col: c });
      }
    }
  }
  return spawns;
}
